import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { workerTeamsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users,
  MagnifyingGlass,
  MapPin,
  Star,
  Briefcase,
  SpinnerGap,
  ArrowLeft,
  ArrowRight,
  CurrencyDollar,
  Funnel
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const BrowseWorkerTeams = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState('');
  const [skillFilter, setSkillFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTeams();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, []);

  const fetchTeams = async (filters = {}) => {
    try {
      setLoading(true);
      const params = {
        search: filters.search || search || undefined,
        skill: filters.skill || skillFilter || undefined,
        location: filters.location || locationFilter || undefined
      };
      
      // Remove undefined params
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await workerTeamsAPI.browse(params);
      if (response.data.success) {
        setTeams(response.data.teams || []);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load teams',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchTeams({ search, skill: skillFilter, location: locationFilter });
  };

  const clearFilters = () => {
    setSearch('');
    setSkillFilter('');
    setLocationFilter('');
    fetchTeams({});
  };

  const TeamCard = ({ team }) => (
    <div 
      className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all cursor-pointer"
      onClick={() => navigate(`/worker-teams/${team.id}`)}
      data-testid={`browse-team-card-${team.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-[#2B4C6F]/10 border-2 border-[#2B4C6F] flex items-center justify-center">
            <Users size={28} weight="bold" className="text-[#2B4C6F]" />
          </div>
          <div>
            <h3 className="font-black text-lg text-gray-900">{team.name}</h3>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users size={14} weight="bold" />
                {team.member_count} members
              </span>
              {team.completed_jobs > 0 && (
                <span className="flex items-center gap-1">
                  <Briefcase size={14} weight="bold" />
                  {team.completed_jobs} jobs
                </span>
              )}
            </div>
          </div>
        </div>
        {team.rating > 0 && (
          <div className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded-lg">
            <Star size={16} weight="fill" className="text-amber-500" />
            <span className="font-bold text-amber-700">{team.rating.toFixed(1)}</span>
          </div>
        )}
      </div>
      
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{team.description}</p>
      
      {team.skills && team.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {team.skills.slice(0, 4).map((skill, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs bg-[#2B4C6F]/10 text-[#2B4C6F] border border-[#2B4C6F]/30">
              {skill}
            </Badge>
          ))}
          {team.skills.length > 4 && (
            <Badge variant="secondary" className="text-xs">
              +{team.skills.length - 4} more
            </Badge>
          )}
        </div>
      )}
      
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="flex items-center gap-4 text-sm">
          {team.location && (
            <span className="flex items-center gap-1 text-gray-500">
              <MapPin size={14} weight="bold" />
              {team.location}
            </span>
          )}
          {team.pricing_type === 'team_rate' && team.team_rate && (
            <span className="flex items-center gap-1 text-green-600 font-bold">
              <CurrencyDollar size={14} weight="bold" />
              ${team.team_rate}/hr
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[#2B4C6F] font-bold text-sm">
          View Team <ArrowRight size={16} weight="bold" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="browse-worker-teams-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-8 border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-4 font-bold"
          >
            <ArrowLeft size={20} weight="bold" />
            Back
          </button>
          <h1 className="text-3xl md:text-4xl font-black">Browse Worker Teams</h1>
          <p className="text-blue-100 text-lg mt-2">Find skilled teams ready to tackle your projects</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search & Filters */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teams by name or skills..."
                className="h-14 text-lg border-2 border-gray-300 rounded-xl"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              className="h-14 px-8 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000]"
              data-testid="search-btn"
            >
              <MagnifyingGlass size={24} weight="bold" className="mr-2" />
              Search
            </Button>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="h-14 px-6 font-bold text-lg rounded-xl border-2 border-black"
            >
              <Funnel size={24} weight="bold" className="mr-2" />
              Filters
            </Button>
          </div>
          
          {showFilters && (
            <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="font-bold text-sm text-gray-700 mb-2 block">Filter by Skill</label>
                <Input
                  value={skillFilter}
                  onChange={(e) => setSkillFilter(e.target.value)}
                  placeholder="e.g., Electrical, Plumbing"
                  className="h-12 border-2 border-gray-300 rounded-xl"
                />
              </div>
              <div>
                <label className="font-bold text-sm text-gray-700 mb-2 block">Filter by Location</label>
                <Input
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  placeholder="e.g., Los Angeles, CA"
                  className="h-12 border-2 border-gray-300 rounded-xl"
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                <Button onClick={handleSearch} className="bg-[#2B4C6F]">Apply Filters</Button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : teams.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
            <Users size={64} weight="light" className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Teams Found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
            <Button onClick={clearFilters} variant="outline">
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            <p className="text-gray-500 mb-4 font-bold">{teams.length} team(s) found</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teams.map((team) => (
                <TeamCard key={team.id} team={team} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BrowseWorkerTeams;
